"use client";

import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { handleFormChange, mergeDateAndTime } from "@/utils/formChange";
import { Loader2, MapPinCheck } from "lucide-react";
import { FC, FormEvent, useEffect, useRef, useState } from "react";
import { useRefresh } from "react-admin";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useNavigate } from "react-router-dom";
import { generateAvailableTimeSlots } from "@/utils/generateTimeSlots";
import DatePicker from "@/components/input/DatePicker";
import StaticDataSelect from "@/components/input/StaticDataSelect";
import axiosInstance from "@/utils/axiosInstance";
import Map from "@/components/map/Map";
import AutocompleteLocation from "@/taxi-dispatch/dispatch/components/AutocompleteLocation";
import { BaseInput, BaseTextArea } from "@/components/input/CustomInputs";
import { DirectionsRenderer } from "@react-google-maps/api";
import { useUserContext } from "@/context/UserContext";
import { calculateTransferPrice } from "@/utils/functions";
import { subYears } from "date-fns";

interface Props {
  title: string;
  description?: string;
}

const libraries: "places"[] = ["places"];

const CustomTransfer: FC<Props> = ({ title }) => {
  const refresh = useRefresh();
  const navigate = useNavigate();
  const { decodedToken } = useUserContext();

  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const [formProp, setFormProp] = useState({
    pickup_location: "",
    pickup_latitude: "",
    pickup_longitude: "",
    dropoff_location: "",
    dropoff_latitude: "",
    dropoff_longitude: "",
    fullName: "",
    flight_number: "",
    client_phone: "",
    date: "",
    time: "",
    passengers: "",
    email: "",
    how_many_adults: 0,
    how_many_children: 0,
    additional_notes: "",
    requested_vehicle_class: "",
    fare: "",
  });

  const [pickupData, setPickupData] = useState<{
    text: string;
    lat: number;
    lng: number;
  } | null>(null);
  const [dropoffData, setDropoffData] = useState<{
    text: string;
    lat: number;
    lng: number;
  } | null>(null);
  const pickupLocationRef = useRef<HTMLInputElement | null>(null);
  const dropoffLocationRef = useRef<HTMLInputElement | null>(null);
  const [directions, setDirectionsData] =
    useState<google.maps.DirectionsResult | null>(null);

  const getPickupData = (data: any) => {
    setPickupData(data);
  };

  const getDropoffData = (data: any) => {
    setDropoffData(data);
  };

  const getDirectionsData = (data: any) => {
    setDirectionsData(data);
  };

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsOpen(true);
  };

  const getCommissionPartner = async () => {
    if (!decodedToken) return;
    try {
      const response = await axiosInstance.get(
        `/referral-agents/users/${decodedToken.user.id}`,
      );
      return response.data.id;
    } catch (error) {
      console.error("Failed to fetch commission partner:", error);
    }
  };

  const calculateRoute = async () => {
    if (!pickupData || !dropoffData) return;

    try {
      const directionsService = new window.google.maps.DirectionsService();
      const results = await directionsService.route({
        origin: { lat: pickupData.lat, lng: pickupData.lng },
        destination: { lat: dropoffData.lat, lng: dropoffData.lng },
        travelMode: google.maps.TravelMode.DRIVING,
      });
      getDirectionsData(results);
    } catch (error) {
      console.error("Error calculating route:", error);
      alert("Failed to calculate route. Please try again.");
    }
  };

  useEffect(() => {
    const today = new Date().toISOString().split("T")[0];
    setFormProp({
      ...formProp,
      date: today,
      time: generateAvailableTimeSlots(new Date())[0],
    });
  }, []);

  const submitForm = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    const duration = directions?.routes[0].legs[0].duration?.value || 0;
    try {
      const response = await axiosInstance.post(`/taxi-orders/tms`, {
        fixed_fee: true,
        pickup_time: mergeDateAndTime(formProp.date, formProp.time),
        status:
          decodedToken?.permissions.user_role === "owner"
            ? "ACCEPTED"
            : "NEW_ORDER",
        pickup: pickupData?.text,
        dropoff: dropoffData?.text,
        pickup_latitude: pickupData?.lat,
        pickup_longitude: pickupData?.lng,
        dropoff_latitude: dropoffData?.lat,
        dropoff_longitude: dropoffData?.lng,
        client_name: formProp.fullName,
        client_phone: "" + formProp.client_phone,
        auto_assign: false,
        scheduled: false,
        order_type: "transfer",
        currency: "EUR",
        email: formProp.email,
        commission_partner_id: await getCommissionPartner(),
        notes: formProp.additional_notes,
        flight_number: formProp.flight_number,
        requested_vehicle_class: formProp.requested_vehicle_class,
        fare: +formProp.fare,
        total_distance_km:
          Number(directions?.routes[0].legs[0].distance?.value) / 1000,
        estimated_duration_minutes: Math.round(duration / 60),
      });

      if (response.data) {
        refresh();

        setTimeout(() => {
          toast("Transfer booked successfully", {
            description:
              "Your custom transfer was booked successfully. Thank you!",
          });
          navigate("/orders");
        }, 2000);
      }
    } catch (error) {
      toast("An error occured. Please try again");
    } finally {
      setLoading(false);
      setIsOpen(false);
    }
  };

  useEffect(() => {
    if (!pickupData || !dropoffData) return;
    calculateRoute();
  }, [pickupData, dropoffData]);

  useEffect(() => {
    const meters = directions?.routes[0].legs[0].distance?.value;
    if (!meters || !formProp.requested_vehicle_class) return;
    const transferPrice = calculateTransferPrice(
      meters / 1000,
      formProp.requested_vehicle_class,
    );
    setFormProp((prev) => {
      return {
        ...prev,
        fare: transferPrice,
      };
    });
  }, [pickupData, dropoffData, formProp.requested_vehicle_class]);

  return (
    <>
      <Button onClick={handleClick}>
        <MapPinCheck className="h-4 w-4" />
        {title}
      </Button>
      <Sheet open={isOpen} onOpenChange={setIsOpen}>
        <SheetContent
          side={"bottom"}
          className="py-5 max-h-[90vh] overflow-auto"
        >
          <form onSubmit={submitForm} className="pl-4 pr-4">
            <div className="grid lg:grid-cols-2 gap-4 items-start gap-x-6">
              <Map
                libraries={libraries}
                coordinates={{
                  pickup_latitude: pickupData?.lat || 0,
                  pickup_longitude: pickupData?.lng || 0,
                  dropoff_latitude: dropoffData?.lat || 0,
                  dropoff_longitude: dropoffData?.lng || 0,
                }}
              >
                {directions && <DirectionsRenderer directions={directions} />}
              </Map>
              <div>
                <div className="mb-4">
                  <p className="text-lg sm:text-4xl font-bold">
                    Book custom transfer
                  </p>
                  <div className="flex items-center gap-x-3">
                    {directions && (
                      <>
                        <p>
                          <span className="font-bold">Distance:</span>{" "}
                          {directions.routes[0].legs[0].distance?.text}
                        </p>
                        <p>
                          <span className="font-bold">Duration:</span>{" "}
                          {directions.routes[0].legs[0].duration?.text}
                        </p>
                      </>
                    )}
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <div className="w-full">
                    <AutocompleteLocation
                      placeholder="Pickup location"
                      location={getPickupData}
                      inputRef={pickupLocationRef}
                    />
                  </div>
                  <div className="w-full">
                    <AutocompleteLocation
                      placeholder="Dropoff location"
                      location={getDropoffData}
                      inputRef={dropoffLocationRef}
                    />
                  </div>
                  <BaseInput
                    id="fullName"
                    placeholder="Your full name"
                    label="Full name"
                    value={formProp.fullName}
                    setProp={setFormProp}
                  />
                  <BaseInput
                    id="email"
                    label="Email"
                    placeholder="Your email"
                    value={formProp.email}
                    setProp={setFormProp}
                  />
                  <BaseInput
                    id="client_phone"
                    label="Phone number"
                    placeholder="Your phone number"
                    type="number"
                    value={formProp.client_phone}
                    setProp={setFormProp}
                  />
                  <BaseInput
                    id="flight_number"
                    label="Flight number"
                    placeholder="Flight number"
                    value={formProp.flight_number}
                    setProp={setFormProp}
                  />
                  <div className="relative w-full">
                    <div className="w-full">
                      <Label htmlFor="date" className="mb-2 text-right">
                        Date *
                      </Label>
                      <DatePicker
                        value={formProp.date}
                        setValue={(e: any) => handleFormChange(e, setFormProp)}
                        minDate={
                          decodedToken?.permissions.user_role === "owner"
                            ? subYears(new Date(), 2)
                            : new Date()
                        }
                      />
                    </div>
                    <div className={`absolute bottom-0 right-0 max-w-40`}>
                      <Select
                        value={formProp.time}
                        onValueChange={(value) =>
                          setFormProp((prev: any) => ({
                            ...prev,
                            time: value,
                          }))
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Time" />
                        </SelectTrigger>
                        <SelectContent>
                          {generateAvailableTimeSlots(
                            decodedToken?.permissions.user_role === "owner"
                              ? null
                              : formProp.date,
                          ).map((time) => (
                            <SelectItem key={time} value={time}>
                              {time}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="w-full">
                    <Label className="mb-2">Vehicle type</Label>
                    <StaticDataSelect
                      id="requested_vehicle_class"
                      value={formProp.requested_vehicle_class}
                      setValue={(e: any) => handleFormChange(e, setFormProp)}
                      data={[
                        { label: "Economy", value: "economy" },
                        { label: "Business", value: "business" },
                        { label: "Van", value: "van" },
                        { label: "Bus", value: "bus" },
                      ]}
                    />
                  </div>
                  <BaseInput
                    id="fare"
                    label="Price"
                    type="number"
                    value={formProp.fare}
                    setProp={setFormProp}
                    placeholder="Transfer price"
                    readonly={decodedToken?.permissions.user_role !== "owner"}
                  />
                  <div className="w-full sm:col-span-2">
                    <BaseTextArea
                      id="additional_notes"
                      label="Notes"
                      value={formProp.additional_notes}
                      setProp={setFormProp}
                    />
                  </div>
                  <div className="sm:col-span-2 place-self-end">
                    <Button
                      type="button"
                      onClick={submitForm}
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        "Book Transfer"
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </SheetContent>
      </Sheet>
    </>
  );
};

export default CustomTransfer;
